// Messages popover.js posts into the overlay iframe: autoplay, pause and the
// prefetched experience payload.
import { constants } from "@/modules/Constants";
import type { PrefetchBucket } from "@/popover/types";

/** The Popover fields these helpers read. */
export interface OverlayState {
  _iFrame: HTMLIFrameElement | null;
  _overlayWantAutoplay: boolean;
  _lastOverlayExperienceId: string | true | null | undefined;
}

// A same-origin overlay's window: its play.js puts videojs there.
type FrameWindow = Window & {
  videojs?: {
    getPlayers?: () => Record<string, { pause?: () => void } | null>;
  };
};

export function requestOverlayAutoplay(
  popover: OverlayState,
  event: MessageEvent,
) {
  if (!popover._overlayWantAutoplay || !popover._iFrame) {
    return;
  }
  let targetWindow = (
    event && event.source ? event.source : null
  ) as Window | null;
  let targetOrigin = event && event.origin ? event.origin : null;
  if (!targetWindow || !targetWindow.postMessage) {
    try {
      targetWindow = popover._iFrame.contentWindow;
      targetOrigin = new URL(popover._iFrame.src, window.location.href).origin;
    } catch (_e) {
      return;
    }
  }
  if (!targetWindow || typeof targetOrigin !== "string" || !targetOrigin) {
    return;
  }
  const msg =
    constants.MESSAGE_PREFIX + JSON.stringify({ type: "overlay_play" });
  try {
    targetWindow.postMessage(msg, targetOrigin);
  } catch (_e) {
    /* ignore */
  }
}

export function experienceIdFromIframeSrc(
  iFrame: HTMLIFrameElement | null,
): string | null {
  if (!iFrame || !iFrame.src || iFrame.src === "about:blank") {
    return null;
  }
  try {
    const u = new URL(iFrame.src, window.location.href);
    const pathParts = u.pathname.replace(/\/+$/, "").split("/");
    const pathId = Number.parseInt(pathParts[pathParts.length - 1], 10);
    if (!isNaN(pathId) && pathId > 0) {
      return String(pathId);
    }
    const q = u.searchParams.get("pix-expid") || u.searchParams.get("expid");
    return q ? String(q) : null;
  } catch {
    return null;
  }
}

export function pauseOverlayIframe(iFrame: HTMLIFrameElement | null) {
  if (!iFrame || iFrame.src.indexOf("about:") === 0) {
    return;
  }
  try {
    const origin = new URL(iFrame.src, window.location.href).origin;
    const msg =
      constants.MESSAGE_PREFIX + JSON.stringify({ type: "overlay_pause" });
    if (iFrame.contentWindow) {
      iFrame.contentWindow.postMessage(msg, origin);
    }
  } catch (_e) {
    /* ignore */
  }
  try {
    const win = iFrame.contentWindow;
    if (!win) {
      return;
    }
    const videos = win.document.getElementsByTagName("video");
    for (let i = 0; i < videos.length; i++) {
      videos[i].pause();
    }
    const vjs = (win as FrameWindow).videojs;
    if (vjs && typeof vjs.getPlayers === "function") {
      const players = vjs.getPlayers();
      for (const id in players) {
        const player = players[id];
        if (player && typeof player.pause === "function") {
          player.pause();
        }
      }
    }
  } catch (_e) {
    /* cross-origin — postMessage only */
  }
}

export function postPrefetchToIframeWindow(
  popover: OverlayState,
  targetWindow: Window | MessageEventSource | null,
  targetOrigin: string,
) {
  if (!targetWindow || !targetWindow.postMessage) {
    return;
  }
  if (typeof targetOrigin !== "string" || targetOrigin.length === 0) {
    return;
  }
  const pm = window.__pixmoto || {};
  const bucket: PrefetchBucket = pm.prefetchedExperiences || {};
  const expId =
    (popover._lastOverlayExperienceId != null &&
      String(popover._lastOverlayExperienceId)) ||
    experienceIdFromIframeSrc(popover._iFrame);
  if (!expId) {
    return;
  }
  const entry = bucket[expId];
  if (!entry || entry.status !== "ok" || entry.data === undefined) {
    return;
  }
  const msg =
    constants.MESSAGE_PREFIX +
    JSON.stringify({
      type: "experience_prefetch",
      expId: String(expId),
      body: entry.data,
    });
  try {
    (targetWindow as Window).postMessage(msg, targetOrigin);
  } catch {
    /* ignore */
  }
}
